import {
    Apartment,
    Badge,
    CalendarMonth,
    Class,
    LocationOn,
    Store,
    Work,
} from '@mui/icons-material'
import { Box } from '@mui/material'
import DataPoint from 'recruiter/components/DataPoint'

import { useTheme } from '@emotion/react'
import type { GetCandidateProfileForRecruiterQuery } from '__generated__/graphql'
import type { FC } from 'react'
import IndustryOrSectorList from 'recruiter/components/IndustryOrSectorList'

interface CandidateDataPointsProps {
    data: GetCandidateProfileForRecruiterQuery
}

const CandidateDataPoints: FC<CandidateDataPointsProps> = ({ data }) => {
    const theme = useTheme()

    const currentWorkExperience = data.candidate?.workExperience?.find(
        (workExperience) => workExperience.isCurrent
    )

    switch (currentWorkExperience?.title?.value) {
        case 'College Student':
        case 'Masters Student':
        case 'Student':
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        marginBottom: 2,
                    }}
                >
                    <DataPoint
                        value="College Student"
                        icon={
                            <Work
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                        title="Employment Type"
                    />
                    {!data.candidate?.education?.[0] && (
                        <DataPoint
                            title="Start Year"
                            value={data.candidate?.startYear ?? ''}
                            icon={
                                <CalendarMonth
                                    htmlColor={
                                        theme.palette.other.secondary_light
                                    }
                                />
                            }
                        />
                    )}
                    <DataPoint
                        title="Graduation Date"
                        value={
                            data.candidate?.education?.[0]?.graduationDate ?? ''
                        }
                        icon={
                            <CalendarMonth
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Analyst Class Year"
                        value={
                            data.candidate?.analystClassYear?.toString() ?? ''
                        }
                        icon={
                            <Class
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                </Box>
            )
        case 'MBA Student':
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        marginBottom: 2,
                    }}
                >
                    <DataPoint
                        value="MBA Student"
                        icon={
                            <Work
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                        title="Employment Type"
                    />
                    {!data.candidate?.education?.[0] && (
                        <DataPoint
                            title="Start Year"
                            value={data.candidate?.startYear ?? ''}
                            icon={
                                <CalendarMonth
                                    htmlColor={
                                        theme.palette.other.secondary_light
                                    }
                                />
                            }
                        />
                    )}
                    <DataPoint
                        title="College"
                        value={
                            data.candidate?.education?.[0]?.college?.value ?? ''
                        }
                        icon={
                            <Apartment
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Graduation Date"
                        value={
                            data.candidate?.education?.[0]?.graduationDate ?? ''
                        }
                        icon={
                            <CalendarMonth
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Analyst Class Year"
                        value={
                            data.candidate?.analystClassYear?.toString() ?? ''
                        }
                        icon={
                            <Class
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                </Box>
            )
        default:
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        marginBottom: 2,
                    }}
                >
                    <DataPoint
                        title="Company"
                        value={currentWorkExperience?.company?.value ?? ''}
                        icon={
                            <Apartment
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Industries"
                        value={
                            <IndustryOrSectorList
                                items={currentWorkExperience?.industries || []}
                                limit={2}
                                sx={{ display: 'flex', alignItems: 'center' }}
                            />
                        }
                        icon={
                            <Work
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Sectors"
                        value={
                            <IndustryOrSectorList
                                items={currentWorkExperience?.sectors || []}
                                sx={{ display: 'flex', alignItems: 'center' }}
                                limit={2}
                            />
                        }
                        icon={
                            <Store
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Title"
                        value={currentWorkExperience?.title?.value ?? ''}
                        icon={
                            <Badge
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Office Location"
                        value={
                            currentWorkExperience?.companyOffice?.officeLocation
                                ?.value ?? ''
                        }
                        icon={
                            <LocationOn
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Start Date"
                        value={currentWorkExperience?.startDate ?? ''}
                        icon={
                            <CalendarMonth
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Analyst Class Year"
                        value={
                            data.candidate?.analystClassYear?.toString() ?? ''
                        }
                        icon={
                            <Class
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                </Box>
            )
    }
}

export default CandidateDataPoints
